import * as C from '../common/constants';
import * as ROUTES from '../common/routes';
import * as yaml from 'js-yaml';
import Button from './Button';
import CircularProgress from '@mui/material/CircularProgress';
import ContentLayout from './ContentLayout';
import ImageContainer from './ImageContainer';
import PrevNextAnswer from './PrevNextAnswer';
import React, { useEffect, useRef } from 'react';
import Share from './Share';
import SurveyAnswerInfo from './SurveyAnswerInfo';
import moment from 'moment';
import { BlockType } from '../common/enums';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { fetchData, clearData } from '../common/app/actions';
import { getText, getPath } from '../common/utils/helpers';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useDynamicTranslations } from '../common/hooks/useTranslations';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      centerContainerStyle: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '30vh',
        flex: 1,
      },
      contentContainerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      titleContainerStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '10px',
      },
      titleStyle: {
        fontWeight: FONT_WEIGHT.bold,
        fontSize: '24px',
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        // whiteSpace: 'nowrap',
        margin: '20px 0',
        textDecoration: 'underline',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      imageContainerStyle: {
        width: '360px',
        minWidth: '200px',
        // height: '200px',
        overflow: 'hidden',
        marginBottom: '20px',
        // background: COLORS.circleGray,
      },
      imageStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
      textContainerStyle: {
        flexDirection: 'column',
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '20px',
        lineHeight: '26px',
        fontFamily: FONTS.alegreya,
        color: COLORS.textBlack,
        marginTop: '4px',
      },
      introTextStyle: {
        marginTop: '6px',
        width: '100%',
      },
      answersContainerStyle: {
        marginTop: '10px',
      },
      answerWrapperStyle: {
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '22px',
        lineHeight: '26px',
        fontFamily: FONTS.alegreya,
        margin: '20px 0 36px 0',
      },
      questionStyle: {
        fontWeight: FONT_WEIGHT.bold,
        color: COLORS.appBlue,
        margin: '10px 0',
      },
      answerStyle: {
        lineHeight: '28px',
        fontWeight: FONT_WEIGHT.regular,
        color: COLORS.textBlack,
        textAlign: 'justify',
        textJustify: 'inter-word',
      },
      infoTitleStyle: {
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        fontSize: '20px',
        color: COLORS.textGray,
        marginBottom: '16px',
      },
      infoContainerStyle: {
        marginTop: '10px',
      },
      dateStyle: {
        width: '100%',
        fontSize: '16px',
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        color: COLORS.textGray,
        textAlign: 'right',
        margin: '20px 0',
      },
      placeAndTimeStyle: {
        display: 'flex',
        flexDirection: 'row',
        fontSize: '16px',
        fontWeight: FONT_WEIGHT.medium,
        fontFamily: FONTS.alegreya,
        color: COLORS.textBlack,
        marginBottom: '10px',
        '& b': {
          fontWeight: FONT_WEIGHT.bold,
          color: COLORS.appBlue,
        },
      },
      placeStyle: {
        marginRight: '20px',
      },
      collectionStyle: {
        fontFamily: FONTS.alegreya,
        fontSize: '16px',
        color: COLORS.textGray,
        paddingBottom: '20px',
        '& b': {
          fontWeight: FONT_WEIGHT.bold,
          color: COLORS.appBlue,
        },
      },
      buttonStyle: {
        padding: 0,
      },
      prevNextStyle: {
        margin: '30px 0 20px 0',
      },
      [`@media (max-width: 840px)`]: {
        titleContainerStyle: {
          flexDirection: 'column',
          margin: '20px 0',
        },
      },
    })
);

const SurveyAnswers = (): JSX.Element => {
  const classes = useStyles();
  const lng = useSelector((state: any) => state.app.lng);
  const location = useLocation();
  const { search, pathname } = location;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    themeId: answerThemeId,
    surveyId: answerSurveyId,
    answerId,
  } = useParams();

  const answersData = useSelector((state: any) => state.app.answersData);

  const lngType = answersData?.survey?.content?.type || lng;
  const shareText = useDynamicTranslations('share_text', lngType);
  const writerLabel = useDynamicTranslations('writer_label', lngType);
  const tellerLabel = useDynamicTranslations('teller_label', lngType);
  const addLabel = useDynamicTranslations('add_label', lngType);

  useEffect(() => {
    dispatch(clearData(C.ANSWERS_DATA));

    dispatch(
      fetchData(C.ANSWERS_DATA, { answerThemeId, answerSurveyId, answerId })
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!answersData) {
    return (
      <div className={classes.centerContainerStyle}>
        <CircularProgress />
      </div>
    );
  }

  const themeName: string = getText(answersData?.theme?.content, 'title', lng);
  const themeLabel: string = `Tēma ${themeName?.toUpperCase()}`;
  const rawUri = answersData?.theme?.content.uri;
  const uri = rawUri.replace('temas/', '/theme/');

  const surveyName: string = getText(
    answersData?.survey?.content,
    'title',
    lng
  );

  const date = answersData.content.date || '';
  const convertedDate = moment(date).format('DD/MM/yyyy HH:mm');

  const myselfData =
    answersData.content.myself && yaml.load(answersData.content.myself);
  const otherData =
    answersData.content.other && yaml.load(answersData.content.other);
  const storyData =
    answersData.content.story && yaml.load(answersData.content.story);

  const surveyData =
    answersData.content.survey && yaml.load(answersData.content.survey);

  const isOneQuestion: boolean = surveyData?.length === 1;

  const getPlaceAndTime = (answer) => {
    const placeText = `<span><b>Vieta:</b> ${answer?.place || ''}</span>`;
    const timeText = `<span><b>Laiks:</b> ${answer?.time || ''}</span>`;

    return (
      <div className={classes.placeAndTimeStyle}>
        {Boolean(answer?.place) && (
          <div
            className={classes.placeStyle}
            dangerouslySetInnerHTML={{ __html: placeText }}
          />
        )}
        {Boolean(answer?.time) && (
          <div
            className={classes.timeStyle}
            dangerouslySetInnerHTML={{ __html: timeText }}
          />
        )}
      </div>
    );
  };

  const getSurveyAnswers = (answer, index) => {
    if (!answer.data) {
      return null;
    }

    const text: string = answer.data.replace(/(?:\r\n|\r|\n)/g, '<br>');

    return (
      <div className={classes.answerWrapperStyle} key={index}>
        {!isOneQuestion && (
          <div className={classes.questionStyle}>{answer.column}</div>
        )}
        {(Boolean(answer.place) || Boolean(answer.time)) &&
          getPlaceAndTime(answer)}
        <div
          className={classes.answerStyle}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      </div>
    );
  };

  const onClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    navigate({
      pathname: `${ROUTES.ADD}/${answerThemeId}/${answerSurveyId}`,
      search,
    });
  };

  const getStoryData = () => {
    const [{ column, data }] = storyData;
    const storyText = data;

    return (
      <>
        <div className={classes.infoTitleStyle}>{column}</div>
        <SurveyAnswerInfo text={storyText} />
      </>
    );
  };

  const backUrl = `${ROUTES.THEME}/${answerThemeId}/${answerSurveyId}`;

  const onThemeClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    navigate({
      pathname: backUrl,
      search,
    });
  };

  const getAnswersData = () => {
    const nr = answersData?.content.collection || '';
    const collectionLabel: string = '<b>Kolekcijas numurs: </b>' + nr;
    const hasImages = Boolean(answersData.images?.length);
    const currentImageData = hasImages && answersData.images[0];

    // const name = myselfData.filter((item) => item.column === 'Vārds');
    // const firstQuestion = surveyData?.[0]?.column;

    // console.log('>>>>>', answersData);

    return (
      <>
        {hasImages && (
          <div className={classes.imageContainerStyle}>
            <ImageContainer data={answersData} thumbSize={BlockType.SMALL} />
          </div>
        )}

        <div className={classes.titleContainerStyle}>
          <div className={classes.titleStyle} onClick={onThemeClick}>
            {surveyName}
          </div>
          <Button
            text={addLabel}
            onClick={onClick}
            className={classes.buttonStyle}
          />
        </div>

        <div className={classes.infoContainerStyle}>
          {Boolean(otherData) && (
            <>
              <div className={classes.infoTitleStyle}>{tellerLabel}</div>
              <SurveyAnswerInfo data={otherData} />
            </>
          )}

          <div className={classes.infoTitleStyle}>{writerLabel}</div>
          <SurveyAnswerInfo data={myselfData} lng={lngType} />

          {Boolean(storyData?.length) && getStoryData()}
        </div>

        {Boolean(nr) && (
          <div
            className={classes.collectionStyle}
            dangerouslySetInnerHTML={{ __html: collectionLabel }}
          />
        )}

        <div className={classes.answersContainerStyle}>
          {surveyData.map(getSurveyAnswers)}
        </div>

        <div className={classes.shareStyle}>
          <Share
            title={surveyName}
            text={shareText || ''}
            image={currentImageData}
            url={window.location.href}
          />
        </div>

        <div className={classes.dateStyle}>{convertedDate}</div>

        <div className={classes.prevNextStyle}>
          <PrevNextAnswer />
        </div>
      </>
    );
  };

  const printDate = moment(date).format('DD-MM-yyyy HH-mm');
  const printData = {
    themeName,
    surveyName,
    date: printDate,
  };

  return (
    <ContentLayout
      themeTitle={themeLabel}
      uri={uri}
      backUrl={backUrl}
      showPrevNext
      printData={printData}
    >
      <div className={classes.contentContainerStyle}>{getAnswersData()}</div>
    </ContentLayout>
  );
};

export default SurveyAnswers;
